import React, { useCallback, useEffect, useState } from "react";
import SectionTitle from "./SectionTitle";
import GuestScrollReveal from "./GuestScrollReveal";
import { GUEST_SECTION_IDS } from "../../data/guestPageData";
import { getNewsList } from "../../api/services/newsService";
import { formatNewsItemForCard } from "../../utils/newsUtils";

const LIMIT = 2;

const NewsSection = () => {
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState("");

    const fetchPage = useCallback(async (pageNum, append) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError("");

        try {
            const { items: rawItems, totalCount: total } = await getNewsList(
                pageNum,
                LIMIT,
            );
            const mapped = rawItems.map(formatNewsItemForCard);

            setItems((prev) => (append ? [...prev, ...mapped] : mapped));
            setPage(pageNum);
            setTotalCount(total);
        } catch (err) {
            setError(err.message || "Не удалось загрузить новости");
            if (!append) {
                setItems([]);
                setTotalCount(0);
            }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, []);

    useEffect(() => {
        fetchPage(1, false);
    }, [fetchPage]);

    const hasMore = page * LIMIT < totalCount;

    const handleShowMore = () => {
        if (!hasMore || loadingMore) {
            return;
        }
        fetchPage(page + 1, true);
    };

    return (
        <section
            className="guest-section guest-section--news"
            id={GUEST_SECTION_IDS.news}
        >
            <GuestScrollReveal>
                <SectionTitle>
                    <span>САМЫЕ ВАЖНЫЕ НОВОСТИ</span>
                    <span className="guest-section-title__accent guest-section-title__accent--block">
                        MEDIEVAL ROLEPLAY
                    </span>
                </SectionTitle>
            </GuestScrollReveal>

            <GuestScrollReveal delay={0.06}>
                <div className="guest-section__content">
                    {loading && items.length === 0 && (
                        <p className="guest-news-status">Загрузка новостей…</p>
                    )}
                    {!loading && error && items.length === 0 && (
                        <p className="guest-news-status">{error}</p>
                    )}
                    {!loading && !error && items.length === 0 && (
                        <p className="guest-news-status">Новостей пока нет</p>
                    )}

                    {items.length > 0 && (
                        <ul className="guest-news-list">
                            {items.map((item, index) => {
                                const isFirst = index === 0;
                                const isLast = index === items.length - 1;

                                return (
                                    <li
                                        key={item.id}
                                        className={[
                                            "guest-news-item",
                                            isFirst && "guest-news-item--first",
                                            isLast && "guest-news-item--last",
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    >
                                        <img
                                            className="guest-news-item__image"
                                            src={item.image}
                                            alt=""
                                            loading="lazy"
                                        />
                                        <div className="guest-news-item__body">
                                            <div
                                                className="guest-news-item__meta"
                                                aria-label={`${item.dayNumber} ${item.month} ${item.year}, ${item.day}, ${item.time}`}
                                            >
                                                <div className="guest-news-item__date-group">
                                                    <span className="guest-news-item__day-num">
                                                        {item.dayNumber}
                                                    </span>
                                                    <span className="guest-news-item__date-text">
                                                        <span className="guest-news-item__month">
                                                            {item.month}
                                                        </span>
                                                        <span className="guest-news-item__year">
                                                            {item.year}
                                                        </span>
                                                    </span>
                                                </div>
                                                <span
                                                    className="guest-news-item__divider"
                                                    aria-hidden="true"
                                                />
                                                <span className="guest-news-item__highlight">
                                                    {item.day}
                                                </span>
                                                <span
                                                    className="guest-news-item__divider"
                                                    aria-hidden="true"
                                                />
                                                <span className="guest-news-item__highlight">
                                                    {item.time}
                                                </span>
                                            </div>
                                            <h3 className="guest-news-item__title">
                                                {item.title}
                                            </h3>
                                            <a
                                                className="guest-news-item__link"
                                                href={item.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Подробнее
                                            </a>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {hasMore && (
                        <button
                            type="button"
                            className="guest-news-more"
                            onClick={handleShowMore}
                            disabled={loadingMore}
                        >
                            {loadingMore ? "Загрузка…" : "Показать больше"}
                        </button>
                    )}
                </div>
            </GuestScrollReveal>
        </section>
    );
};

export default NewsSection;
